import { useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { useRealtime } from "@/hooks/useRealtime";

/**
 * useMessagePing — global sound + toast for every incoming portal message.
 *
 * Listens to the SSE realtime stream for {type: "message_new"} events (which
 * the backend publishes for both directions: admin→client and client→staff).
 *
 * Behavior:
 *   • Plays a soft two-tone Web-Audio "ping" (never a full-volume alert).
 *   • Shows a sonner toast with sender + preview, click routes to the thread.
 *   • Skips playback if we're already on the exact messages page for that client,
 *     since the chat view already visibly updates.
 *   • Respects a persisted mute toggle (localStorage "cq_msg_ping_muted").
 *
 * Mount ONCE at the App level (inside AuthProvider + BrowserRouter).
 */

const MUTE_KEY = "cq_msg_ping_muted";

function isMuted() {
  try { return localStorage.getItem(MUTE_KEY) === "1"; } catch { return false; }
}

function getAudioCtx() {
  try {
    if (typeof window === "undefined") return null;
    if (!window.__cqMsgAudio) {
      const C = window.AudioContext || window.webkitAudioContext;
      if (!C) return null;
      window.__cqMsgAudio = new C();
    }
    if (window.__cqMsgAudio.state === "suspended") {
      window.__cqMsgAudio.resume().catch(() => {});
    }
    return window.__cqMsgAudio;
  } catch { return null; }
}

/**
 * Warm, iOS-style two-tone chime: G5 → C6 with a soft sine and quick decay.
 * Falls back to a single beep if a filter isn't available.
 */
function playPing() {
  if (isMuted()) return;
  const ctx = getAudioCtx();
  if (!ctx) return;
  try {
    const now = ctx.currentTime;
    const master = ctx.createGain();
    master.gain.value = 0.16;
    master.connect(ctx.destination);

    const chime = (freq, start, dur) => {
      const o = ctx.createOscillator();
      const g = ctx.createGain();
      o.type = "sine";
      o.frequency.value = freq;
      g.gain.setValueAtTime(0.0001, start);
      g.gain.exponentialRampToValueAtTime(1.0, start + 0.02);
      g.gain.exponentialRampToValueAtTime(0.0001, start + dur);
      o.connect(g); g.connect(master);
      o.start(start); o.stop(start + dur + 0.05);
    };
    // Two-tone: G5 (784Hz) → C6 (1046.5Hz)
    chime(783.99, now,        0.28);
    chime(1046.5, now + 0.14, 0.34);
  } catch { /* silent */ }
}

export default function useMessagePing() {
  const { user } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const locRef = useRef(loc);
  locRef.current = loc;

  // Unlock the AudioContext on first user gesture so autoplay policies don't
  // block the first ping.
  useEffect(() => {
    if (!user) return;
    const unlock = () => { getAudioCtx(); };
    window.addEventListener("click", unlock, { once: true });
    window.addEventListener("keydown", unlock, { once: true });
    window.addEventListener("touchstart", unlock, { once: true });
    return () => {
      window.removeEventListener("click", unlock);
      window.removeEventListener("keydown", unlock);
      window.removeEventListener("touchstart", unlock);
    };
  }, [user?.id]);

  useRealtime((evt) => {
    if (!user) return;
    if (evt?.type !== "message_new" || !evt.data) return;
    const d = evt.data;
    // Never self-notify (belt-and-suspenders, backend also filters)
    if (d.sender_id === user.id) return;

    // Skip audio if the user is already looking at the exact thread.
    const path = locRef.current?.pathname || "";
    const isClientOnOwnThread = user.role === "client" && path.startsWith("/portal/messages");
    const isStaffOnClientThread =
      ["super_admin", "admin", "team_member", "credit_specialist"].includes(user.role) &&
      path.includes(`/admin/clients/${d.client_id}`);

    if (!isClientOnOwnThread && !isStaffOnClientThread) {
      playPing();
    }

    const label = d.sender_name || "New message";
    const preview = (d.preview || "").trim() || "sent you a new message";
    const goto = user.role === "client"
      ? "/portal/messages"
      : `/admin/clients/${d.client_id}`;

    toast(`💬 ${label}`, {
      description: preview.length > 100 ? preview.slice(0, 97) + "…" : preview,
      duration: 6000,
      action: {
        label: "Open",
        onClick: () => nav(goto),
      },
    });
  }, !!user);
}

/** Small helper for a settings UI to flip the mute state. */
export function toggleMessagePingMute() {
  const next = !isMuted();
  try { localStorage.setItem(MUTE_KEY, next ? "1" : "0"); } catch { /* no-op */ }
  return next;
}

export function isMessagePingMuted() {
  return isMuted();
}
